import React from "react";
import { useLocation, Navigate, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { rolesConfig } from "../rolesConfig";

export default function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const reduxUser = useSelector((state) => state.user?.user);

  if (isLoading) {
    return (
      <div
        className="min-h-screen h-[100svh] w-full flex flex-col items-center justify-center bg-slate-50 gap-4"
        style={{
          height: "100svh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          backgroundColor: "#f8fafc",
          color: "#334155",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div className="w-11 h-11 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
        <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#334155" }}>
          Verifying session…
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";
    window.location.href = `${backendUrl}/api/auth/login`;
    return null;
  }

  if (reduxUser) {
    const normalizedUser = {
      ...reduxUser,
      role: reduxUser.role || reduxUser.clientRole || "Viewer",
    };
    localStorage.setItem("user", JSON.stringify(normalizedUser));
  }

  // Check specific allowedRoles if passed to ProtectedRoute
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = (reduxUser?.role || reduxUser?.clientRole || "").toLowerCase();
    const hasAllowedRole = allowedRoles.some(
      (r) =>
        r.toLowerCase() === userRole ||
        userRole === "super_admin" ||
        userRole === "admin"
    );
    if (!hasAllowedRole) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      const roleConfig = rolesConfig[user.role];
      const fallbackRoute = roleConfig?.landingRoute || "/dashboard";

      if (!roleConfig) {
        if (location.pathname !== "/dashboard") {
          return <Navigate to="/dashboard" replace />;
        }
      } else {
        if (location.pathname === "/") {
          return <Navigate to={fallbackRoute} replace />;
        }

        const isAllowed =
          roleConfig.allowedRoutes.includes("*") ||
          roleConfig.allowedRoutes.some((routePattern) =>
            matchPath(
              {
                path: routePattern,
                end: false,
              },
              location.pathname
            )
          );

        if (!isAllowed) {
          console.warn(
            `Access denied for role "${user.role}" on path "${location.pathname}". Redirecting to ${fallbackRoute}.`
          );
          if (location.pathname !== fallbackRoute) {
            return <Navigate to={fallbackRoute} replace />;
          }
        }
      }
    }
  } catch (error) {
    console.error("Auth role check failed:", error);
    if (location.pathname !== "/dashboard") {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
